// Project detail → Settings (architecture sprint-11 §9.4; sprint 12: no membership mode, every project is
// members-only). Admins edit everything; a delegated Supervisor sees the same rows with Admin-only rows locked
// and no mode control. The server decides every edit right (canEdit / canChooseMode / canReset).
import { isSettingKey } from '@shared/settings-catalogue';
import { useState } from 'react';
import { toast } from 'sonner';
import { projectItem, withAlarmRules } from '@/components/settings/setting-items';
import { SettingGroups } from '@/components/settings/SettingGroups';
import { Skeleton } from '@/components/ui/skeleton';
import { useProjectSettings, useResetProjectSetting, useSetProjectSetting } from '@/hooks/use-settings';
import type { SettingModeValue } from '@/types/settings';


export function ProjectSettingsTab({ projectId }: { projectId: string }): JSX.Element {
  const settings = useProjectSettings(projectId);
  const set = useSetProjectSetting(projectId);
  const reset = useResetProjectSetting(projectId);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  if (!settings.data) return <Skeleton className="h-64 w-full" />;
  const { viewer, items } = settings.data;
  const rows = withAlarmRules(items.filter((i) => i.writeVia === 'settings').map((i) => projectItem(i, viewer)));

  function save(key: string, value: unknown, mode?: SettingModeValue): void {
    if (!isSettingKey(key)) return;
    setPendingKey(key);
    set.mutate({ key, value, ...(mode ? { mode } : {}) }, { onSuccess: () => toast.success('Setting saved.'), onSettled: () => setPendingKey(null) });
  }

  return (
    <SettingGroups
      items={rows}
      context="project"
      pendingKey={pendingKey}
      onChange={(key, value) => save(key, value)}
      onModeChange={viewer === 'admin' ? (key, mode, value) => save(key, value, mode) : undefined}
      onReset={(key) => {
        if (!isSettingKey(key)) return;
        setPendingKey(key);
        reset.mutate(key, { onSettled: () => setPendingKey(null) });
      }}
    />
  );
}
